/**
 * Golden Bat — „Unmatched Adventures: Tales to Amaze".
 *
 * THE FIRST SUPERHERO: kým hráč v tomto ťahu nemanévroval, Golden Batove
 * útoky majú +2. Sidekicka nemá.
 */

import { shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import {
  askChoice,
  dealDamage,
  discardRandom,
  discardToDeck,
  drawCard,
  emit,
  getMap,
  getPlayer,
  returnFromDiscard,
  shuffleDeck,
} from '../ops.js';
import { areEnemies } from '../players.js';
import { nextInt } from '../rng.js';
import type { PlayerIndex } from '../types.js';
import { feint, skirmish } from './common.js';
import { askMove, canMove, opponentOf } from './helpers.js';
import { terrifyingRoar } from './tRex.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const BAT = 'golden-bat';
const FORTRESS = 'golden-bat.alpine-fortress';
const SIGHT = 'golden-bat.sight-beyond-sight';

const batOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === BAT) ?? null;

/** „Deal 1 damage to each opposing fighter in Golden Bat's zone. If you won the combat, deal 2 damage instead." */
const punchToShakeTheEarth: CardEffect = {
  after: (e) => {
    const bat = batOf(e.ctx, e.player);
    if (!bat) return;
    const map = getMap(e.ctx);
    const targets = e.ctx.state.fighters
      .filter((f) => areEnemies(e.ctx.state, f.owner, e.player) && shareZone(map, f.spaceId, bat.spaceId))
      .map((f) => f.id);
    for (const id of targets) dealDamage(e.ctx, id, e.won ? 2 : 1);
  },
};

/** Draw 2 cards. Move Golden Bat up to 4 spaces. — zvyšok Alpine Fortress po výbere karty. */
function fortressRest(ctx: Ctx, player: PlayerIndex, cardId: string): void {
  drawCard(ctx, player);
  drawCard(ctx, player);
  const bat = batOf(ctx, player);
  if (!bat || !canMove(ctx, bat.id, 4, false)) return;
  askChoice(ctx, {
    kind: 'move',
    player,
    source: cardId,
    label: 'Alpine Fortress: presuň Golden Bata až o 4',
    optional: true,
    fighters: [bat.id],
    max: 4,
    through: false,
  });
}

/**
 * „Choose a card in your discard pile and shuffle it into your deck. Draw 2
 * cards. Move Golden Bat up to 4 spaces." Samotná schéma v odhode ešte nie je.
 */
const alpineFortress: CardEffect = {
  scheme: (e) => {
    const discard = getPlayer(e.ctx.state, e.player).discard.slice();
    if (discard.length === 0) {
      fortressRest(e.ctx, e.player, e.cardId);
      return;
    }
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Alpine Fortress: vyber kartu z odhodu, ktorá sa zamieša do balíčka',
      optional: false,
      from: 'discard',
      cards: discard,
      purpose: 'effect',
    });
  },
  onCardChosen: (ctx, choice, cardId) => {
    discardToDeck(ctx, choice.player, cardId);
    shuffleDeck(ctx, choice.player);
    fortressRest(ctx, choice.player, choice.source ?? cardId);
  },
};

/** „Place Golden Bat in any space." */
const arriveJustInTime: CardEffect = {
  scheme: (e) => {
    const bat = batOf(e.ctx, e.player);
    if (!bat) return;
    const occupied = new Set(e.ctx.state.fighters.map((f) => f.spaceId));
    const spaces = getMap(e.ctx).spaces.map((s) => s.id).filter((id) => !occupied.has(id));
    if (spaces.length === 0) return;
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Arrive Just in Time: polož Golden Bata na ľubovoľný priestor',
      optional: false,
      fighterId: bat.id,
      spaces,
    });
  },
};

/** „If you won the combat, return a random card in your discard pile to your hand." */
const insightOfTheAncients: CardEffect = {
  after: (e) => {
    if (!e.won) return;
    const discard = getPlayer(e.ctx.state, e.player).discard;
    if (discard.length === 0) return;
    returnFromDiscard(e.ctx, e.player, [discard[nextInt(e.ctx.state, discard.length)]!]);
  },
};

/** Zdieľa Golden Bat / Bloody Mary zónu s priestorom, kde začala ťah? */
export function leftStartZone(e: EffectCtx): boolean {
  if (!e.fighterId) return false;
  const fighter = e.ctx.state.fighters.find((f) => f.id === e.fighterId);
  const start = e.ctx.state.turn.startSpaces[e.fighterId];
  // `shareZone` vracia pre ten istý priestor false — kto sa nepohol, zónu neopustil.
  return Boolean(fighter && start && fighter.spaceId !== start && !shareZone(getMap(e.ctx), fighter.spaceId, start));
}

/**
 * „DURING: If Golden Bat shares no zones with the space he started this turn
 * in, this card's value is 4 instead. AFTER: Move Golden Bat up to 5 spaces."
 */
const flashOfGoldenLight: CardEffect = {
  during: (e, value) => (leftStartZone(e) ? 4 : value),
  after: (e) => {
    const bat = batOf(e.ctx, e.player);
    if (bat) askMove(e, { fighters: [bat.id], max: 5, label: 'Like a Flash of Golden Light: presuň Golden Bata až o 5' });
  },
};

/**
 * „Look at the top 3 cards of your opponent's deck. Put them back in any
 * order." Hráč postupne vyberá, ktorá karta pôjde vyššie; posledná ostane pod
 * nimi. Karty vidí len on (redakcia voľby `from: 'deck'`).
 */
const sightBeyondSight: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null) return;
    const top = getPlayer(e.ctx.state, opponent).deck.slice(0, 3);
    if (top.length < 2) return;
    askSight(e.ctx, e.player, opponent, e.cardId, top);
  },
  onCardChosen: (ctx, choice, cardId) => {
    const owner = choice.owner ?? choice.player;
    const deck = getPlayer(ctx.state, owner).deck;
    const rest = (choice.cards ?? []).filter((id) => id !== cardId);
    // Nevybrané karty ležia súvisle hneď pod už uloženými — vybraná ide pred ne.
    const slot = Math.min(...(choice.cards ?? []).map((id) => deck.indexOf(id)).filter((i) => i >= 0));
    deck.splice(deck.indexOf(cardId), 1);
    deck.splice(slot, 0, cardId);
    if (rest.length >= 2) askSight(ctx, choice.player, owner, choice.source ?? cardId, rest);
    else emit(ctx, { type: 'deckReordered', player: choice.player, owner });
  },
};

function askSight(ctx: Ctx, player: PlayerIndex, owner: PlayerIndex, source: string, cards: string[]): void {
  askChoice(ctx, {
    kind: 'card',
    player,
    source,
    label: 'Sight Beyond Sight: vyber kartu, ktorá pôjde navrch (z tých, čo ostali)',
    optional: true,
    from: 'deck',
    owner,
    cards,
    purpose: 'effect',
    left: cards.length,
    onSkip: { kind: 'effect', effect: SIGHT, data: String(owner) },
  });
}

/** „Your opponent discards 1 random card." */
const vaporizingEyebeams: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (opponent !== null) discardRandom(e.ctx, opponent, true);
  },
};

export const goldenBatCards: EffectRegistry = {
  'golden-bat.a-punch-to-shake-the-earth': punchToShakeTheEarth,
  [FORTRESS]: alpineFortress,
  'golden-bat.arrive-just-in-time': arriveJustInTime,
  'golden-bat.imposing-presence': feint,
  'golden-bat.insight-of-the-ancients': insightOfTheAncients,
  'golden-bat.like-a-flash-of-golden-light': flashOfGoldenLight,
  [SIGHT]: {
    ...sightBeyondSight,
    // Preskočenie: poradie ostáva, ale nazretie sa v priebehu ukáže.
    onFollow: (ctx, choice, owner) => emit(ctx, { type: 'deckReordered', player: choice.player, owner: Number(owner) }),
  },
  'golden-bat.skirmish': skirmish(2),
  'golden-bat.terrifying-roar': terrifyingRoar,
  'golden-bat.vaporizing-eyebeams': vaporizingEyebeams,
  // He Laughs at Your Feebleness, Super Strength: bez efektu.
};

export const goldenBatSpecial: HeroSpecial = {
  name: 'The First Superhero',
  /** „If you haven't taken a Maneuver action this turn, add +2 to the value of Golden Bat's attacks." */
  attackModifier: (ctx, _player, fighterId) => {
    const bat = ctx.state.fighters.find((f) => f.id === fighterId);
    if (bat?.defId !== BAT) return 0;
    return ctx.state.turn.actionsUsed.includes('maneuver') ? 0 : 2;
  },
};
